import type { Document } from '../../../../../js/api';
import type { DocumentRenderHelpers } from '../../../components/document-type-access';
import { ExpirationBadge } from '../../../components/expiration-badge';
import { formatDateTo24Hr } from '../../../utils/date';
import { EMPTY_VALUE, formatProviderDate } from './providerDisplay';
import { ExpirationDate, RecordCard } from './ProviderDrawerPrimitives';

const UPLOADED_FORMAT = 'MMM d, yyyy h:mm a';

/**
 * One document of a document-card section: title = its label (else the file name),
 * the file name as the one-click view link, when it was uploaded and by whom, issue /
 * expiry dates
 * only when the document has them (a non-expirable type never does), an "Expired" /
 * "Expiring soon" badge top-right when its expiry date calls for one, and the paperclip
 * menu bottom-right. See patterns/add-in-context-document-access.md.
 */
export function DocumentRecordCard({
  document,
  renderLink,
  renderMenu,
  testIdPrefix,
  leading,
}: {
  document: Document;
  renderLink: DocumentRenderHelpers['renderLink'];
  renderMenu: DocumentRenderHelpers['renderMenu'];
  /** `<prefix>-<document id>` becomes the file link's test id. */
  testIdPrefix: string;
  /** Upper-left of the card, left of the name — the selection checkbox. */
  leading?: React.ReactNode;
}) {
  return (
    <RecordCard
      title={document.label || document.original_filename}
      leading={leading}
      rightElement={
        <ExpirationBadge
          expiresOn={document.expires_on}
          data-testid={`${testIdPrefix}-${document.id}-expiration`}
        />
      }
      footer={renderMenu(document)}
      fields={[
        {
          label: 'File',
          value: renderLink(document, `${testIdPrefix}-${document.id}`, {
            menu: false,
            text: document.original_filename,
          }),
        },
        {
          label: 'Uploaded',
          // when, and by whom
          value: [
            formatDateTo24Hr(document.uploaded_at, UPLOADED_FORMAT) || EMPTY_VALUE,
            document.created_by_name ? `by ${document.created_by_name}` : '',
          ]
            .filter(Boolean)
            .join(' '),
        },
        ...(document.issued_on
          ? [{ label: 'Issued', value: formatProviderDate(document.issued_on) }]
          : []),
        ...(document.expires_on
          ? [
              {
                label: 'Expires',
                value: <ExpirationDate value={document.expires_on} showRelative />,
              },
            ]
          : []),
      ]}
    />
  );
}
